import React, { useState, useEffect, useCallback } from 'react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { Transaction } from '@solana/web3.js';
import { blinksApi } from '../services/blinksApi';

interface Play {
  playId: string;
  crateId: string;
  crateName?: string;
  amount: number;
  isWinner: boolean;
  winAmount?: number;
  isClosed: boolean;
  transactionSignature?: string;
  resolvedAt: Date;
  createdAt: Date;
}

const History: React.FC = () => {
  const { publicKey, signTransaction } = useWallet();
  const { connection } = useConnection();
  const [plays, setPlays] = useState<Play[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const [closingBatch, setClosingBatch] = useState(false);
  const [errorModal, setErrorModal] = useState<{ title: string; message: string } | null>(null);
  const [successModal, setSuccessModal] = useState<{ title: string; message: string } | null>(null);

  const loadHistory = useCallback(async () => {
    if (!publicKey) return;

    setLoading(true);
    setError(null);
    try {
      const data = await blinksApi.getPlayHistory(publicKey.toString());
      setPlays(data.plays);
    } catch (err: any) {
      setError(err.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [publicKey]);

  useEffect(() => {
    if (publicKey) {
      loadHistory();
    } else {
      setPlays([]);
      setLoading(false);
    }
  }, [publicKey, loadHistory]);

  const handleClosePlay = async (playId: string) => {
    if (!publicKey || !signTransaction) return;

    setClosing(true);
    try {
      const { transaction: transactionBase64 } = await blinksApi.closePlay(
        playId,
        publicKey.toString()
      );

      const transaction = Transaction.from(
        Buffer.from(transactionBase64, 'base64')
      );

      const signedTx = await signTransaction(transaction);
      const signature = await connection.sendRawTransaction(
        signedTx.serialize()
      );

      await connection.confirmTransaction(signature, 'confirmed');
      
      // Update database after confirmation
      try {
        await blinksApi.closePlayResult(playId, signature);
      } catch (err: any) {
        console.warn('Failed to update database:', err);
        // Continue anyway - the transaction succeeded
      }
      
      // Reload history to update closed status
      await loadHistory();
      
      setSuccessModal({
        title: 'Play Closed',
        message: `Successfully closed play! Transaction signature: ${signature}`
      });
    } catch (err: any) {
      const errorMessage = err.message || err.toString() || 'Unknown error';
      console.error('Error closing play:', err);
      setErrorModal({
        title: 'Error Closing Play',
        message: errorMessage
      });
    } finally {
      setClosing(false);
    }
  };

  const handleCloseAll = async () => {
    if (!publicKey || !signTransaction) return;

    // Get all unclosed plays
    const unclosedPlays = plays.filter(p => !p.isClosed);
    
    if (unclosedPlays.length === 0) {
      setErrorModal({
        title: 'No Closable Plays',
        message: 'No closable plays found. All plays may already be closed.'
      });
      return;
    }

    // Limit to first 20 plays (backend limit)
    const MAX_BATCH_SIZE = 20;
    const playsToClose = unclosedPlays.slice(0, MAX_BATCH_SIZE);
    const remainingCount = unclosedPlays.length - MAX_BATCH_SIZE;

    const confirmMessage = remainingCount > 0
      ? `Close ${playsToClose.length} play(s) to reclaim rent? (${remainingCount} remaining - click again to close more)`
      : `Close ${playsToClose.length} play(s) to reclaim rent?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setClosingBatch(true);
    try {
      const { transaction: transactionBase64, playIds, count } = await blinksApi.closePlaysBatch(
        publicKey.toString(),
        playsToClose.map(p => p.playId)
      );

      const transaction = Transaction.from(
        Buffer.from(transactionBase64, 'base64')
      );

      const signedTx = await signTransaction(transaction);
      const signature = await connection.sendRawTransaction(
        signedTx.serialize()
      );

      await connection.confirmTransaction(signature, 'confirmed');
      
      // Update database after confirmation
      try {
        await blinksApi.closePlaysBatchResult(playIds, signature);
      } catch (err: any) {
        console.warn('Failed to update database:', err);
        // Continue anyway - the transaction succeeded
      }
      
      // Reload history to update closed status
      await loadHistory();
      
      // Calculate remaining after this batch (use the original remainingCount from before the transaction)
      const stillRemaining = Math.max(0, remainingCount);
      const successMessage = stillRemaining > 0
        ? `Successfully closed ${count} play(s)! ${stillRemaining} remaining - click "Refund All Gas" again to close more. Transaction signature: ${signature}`
        : `Successfully closed ${count} play(s)! Transaction signature: ${signature}`;
      
      setSuccessModal({
        title: 'Plays Closed',
        message: successMessage
      });
    } catch (err: any) {
      const errorMessage = err.message || err.toString() || 'Unknown error';
      console.error('Error closing plays:', err);
      setErrorModal({
        title: 'Error Closing Plays',
        message: errorMessage
      });
    } finally {
      setClosingBatch(false);
    }
  };

  const formatTokenAmount = (amount: number) => {
    if (amount >= 1000) {
      return `${(amount / 1000).toFixed(2)}K`;
    }
    return amount.toFixed(2);
  };

  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString();
  };

  const formatTokenAmountFull = (amount: number) => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(2)}M`;
    }
    if (amount >= 1000) {
      return `${(amount / 1000).toFixed(2)}K`;
    }
    return amount.toFixed(2);
  };

  // Calculate stats from plays
  const calculateStats = () => {
    const totalPlays = plays.length;
    const totalSpent = plays.reduce((sum, play) => sum + play.amount, 0);
    const totalWon = plays
      .filter(p => p.winAmount)
      .reduce((sum, play) => sum + (play.winAmount || 0), 0);
    const netPL = totalWon - totalSpent;
    const wins = plays.filter(p => p.winAmount && p.winAmount > 0).length;
    const losses = totalPlays - wins;
    const winRate = totalPlays > 0 ? (wins / totalPlays) * 100 : 0;
    const averageWin = wins > 0 
      ? plays
          .filter(p => p.winAmount && p.winAmount > 0)
          .reduce((sum, p) => sum + (p.winAmount || 0), 0) / wins
      : 0;
    const largestWin = plays
      .filter(p => p.winAmount && p.winAmount > 0)
      .reduce((max, p) => Math.max(max, p.winAmount || 0), 0);

    return {
      totalPlays,
      totalSpent,
      totalWon,
      netPL,
      wins,
      losses,
      winRate,
      averageWin,
      largestWin,
    };
  };

  const stats = calculateStats();
  const unclosedCount = plays.filter(p => p.winAmount && !p.isClosed).length;

  if (!publicKey) {
    return (
      <div className="history-container">
        <div className="history-content">
          <h2>Play History</h2>
          <p>Please connect your wallet to view your play history.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="history-container">
      <div className="history-content">
        {loading ? (
          <p>Loading history...</p>
        ) : error ? (
          <p className="error">Error: {error}</p>
        ) : plays.length === 0 ? (
          <p>No play history found.</p>
        ) : (
          <>
            {/* Stats Section */}
            <div className="stats-section">
              <h2 className="stats-header">Statistics</h2>
              <div className="stats-container">
                <div className="stats-group">
                  <div className="stat-item">
                    <span className="stat-label">Total Turnover</span>
                    <span className="stat-value">{formatTokenAmountFull(stats.totalSpent)} $BLOOT</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Total Spent</span>
                    <span className="stat-value">{formatTokenAmountFull(stats.totalSpent)} $BLOOT</span>
                  </div>
                </div>
                <div className="stats-group">
                  <div className="stat-item">
                    <span className="stat-label">Total Won</span>
                    <span className="stat-value">{formatTokenAmountFull(stats.totalWon)} $BLOOT</span>
                  </div>
                  <div className={`stat-item ${stats.netPL >= 0 ? 'positive' : 'negative'}`}>
                    <span className="stat-label">Net P&L</span>
                    <span className="stat-value">
                      {stats.netPL >= 0 ? '+' : ''}{formatTokenAmountFull(stats.netPL)} $BLOOT
                    </span>
                  </div>
                </div>
                <div className="stats-group">
                  <div className="stat-item">
                    <span className="stat-label">Total Plays</span>
                    <span className="stat-value">{stats.totalPlays}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Wins</span>
                    <span className="stat-value positive">{stats.wins}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Losses</span>
                    <span className="stat-value negative">{stats.losses}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Win Rate</span>
                    <span className="stat-value">{stats.winRate.toFixed(1)}%</span>
                  </div>
                  {stats.averageWin > 0 && (
                    <div className="stat-item">
                      <span className="stat-label">Avg Win</span>
                      <span className="stat-value">{formatTokenAmountFull(stats.averageWin)} $BLOOT</span>
                    </div>
                  )}
                  {stats.largestWin > 0 && (
                    <div className="stat-item">
                      <span className="stat-label">Largest Win</span>
                      <span className="stat-value positive">{formatTokenAmountFull(stats.largestWin)} $BLOOT</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Play History Header */}
            <div className="history-header">
              <h2>Play History</h2>
              {unclosedCount > 0 && (
                <button
                  className="refund-all-button"
                  onClick={handleCloseAll}
                  disabled={closingBatch}
                >
                  {closingBatch ? 'Closing...' : `Refund All Gas (${unclosedCount})`}
                </button>
              )}
            </div>

            <div className="history-table-container">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Crate</th>
                  <th>Entry Cost</th>
                  <th>Win Amount</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {plays.map((play) => (
                  <tr key={play.playId}>
                    <td>{formatDate(play.resolvedAt)}</td>
                    <td className="crate-id">{play.crateName || play.crateId.slice(0, 8) + '...'}</td>
                    <td>{formatTokenAmount(play.amount)} $BLOOT</td>
                    <td>
                      {play.winAmount
                        ? `${formatTokenAmount(play.winAmount)} $BLOOT`
                        : '-'}
                    </td>
                    <td>
                      <span className={`status ${play.winAmount ? 'settled' : 'open'}`}>
                        {play.winAmount ? '✅ Settled' : '⏳ Open'}
                      </span>
                    </td>
                    <td>
                      {play.winAmount && !play.isClosed ? (
                        <button
                          className="close-button"
                          onClick={() => handleClosePlay(play.playId)}
                          disabled={closing}
                        >
                          {closing ? 'Closing...' : 'Close'}
                        </button>
                      ) : play.isClosed ? (
                        <span className="closed-text">Closed</span>
                      ) : (
                        <span className="closed-text">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      <style>{`
        .history-container {
          min-height: 100vh;
          padding: 2rem;
          color: white;
        }

        .history-content {
          max-width: 1200px;
          margin: 0 auto;
        }

        .history-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
        }

        .history-header h2 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 700;
          font-family: var(--font-display);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #ffffff;
        }

        .stats-section {
          margin-bottom: 2rem;
        }

        .stats-header {
          margin: 0 0 1rem 0;
          font-size: 1.5rem;
          font-weight: 700;
          font-family: var(--font-display);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #ffffff;
        }

        .stats-container {
          background: rgba(10, 10, 10, 0.6);
          border: 1px solid #333;
          border-radius: 4px;
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 24px;
          backdrop-filter: blur(20px);
        }

        .stats-group {
          display: flex;
          flex-wrap: wrap;
          gap: 32px 48px;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          gap: 6px;
          flex: 1;
          min-width: 140px;
        }

        .stat-item.positive .stat-value {
          color: #00FF9C;
        }

        .stat-item.negative .stat-value {
          color: #f87171;
        }

        .stat-label {
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #888;
          font-family: var(--font-display);
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #ffffff;
          font-family: var(--font-mono);
        }

        .stat-value.positive {
          color: #00FF9C;
        }

        .stat-value.negative {
          color: #f87171;
        }

        .refund-all-button {
          background: transparent;
          color: #ffffff;
          border: 1px solid #ffffff;
          border-radius: 4px;
          padding: 12px 20px;
          font-family: var(--font-display);
          font-weight: 700;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          cursor: pointer;
          transition: all 0.2s;
          position: relative;
          overflow: hidden;
        }

        .refund-all-button:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.05);
          border-color: #ffffff;
        }

        .refund-all-button:active:not(:disabled) {
          transform: translateY(0);
        }

        .refund-all-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .history-table-container {
          overflow-x: auto;
          background: rgba(10, 10, 10, 0.6);
          border: 1px solid #333;
          border-radius: 4px;
          padding: 0;
          margin-top: 2rem;
          backdrop-filter: blur(20px);
        }

        .history-table {
          width: 100%;
          border-collapse: collapse;
        }

        .history-table thead {
          background: transparent;
          border-bottom: 1px solid #333;
        }

        .history-table th {
          padding: 16px;
          text-align: left;
          font-weight: 700;
          font-family: var(--font-display);
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #888;
          border-bottom: 1px solid #333;
          white-space: nowrap;
        }

        .history-table th:not(:last-child) {
          border-right: 1px solid #333;
        }

        .history-table td {
          padding: 16px;
          border-bottom: 1px solid #333;
          color: #ffffff;
          font-size: 0.9rem;
        }

        .history-table td:nth-child(1) {
          font-family: var(--font-mono);
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.8);
        }

        .history-table td:nth-child(3),
        .history-table td:nth-child(4) {
          font-family: var(--font-mono);
          font-weight: 600;
          color: #ffffff;
        }

        .history-table tbody tr {
          transition: all 0.2s ease;
          background: transparent;
        }

        .history-table tbody tr:hover {
          background: rgba(255, 255, 255, 0.02);
        }

        .history-table tbody tr:last-child td {
          border-bottom: none;
        }

        .history-table td:not(:last-child) {
          border-right: 1px solid #333;
        }

        .crate-id {
          font-family: var(--font-mono);
          font-size: 0.85rem;
          color: #ffffff;
        }


        .status {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border: 1px solid;
          font-family: var(--font-display);
        }

        .status.settled {
          background: rgba(0, 255, 156, 0.1);
          color: #00FF9C;
          border-color: #00FF9C;
        }

        .status.open {
          background: rgba(255, 215, 0, 0.1);
          color: #FFD700;
          border-color: #FFD700;
        }

        .close-button {
          background: transparent;
          color: #ffffff;
          border: 1px solid #333;
          border-radius: 4px;
          padding: 6px 12px;
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-family: var(--font-display);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .close-button:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.05);
          border-color: #444;
        }

        .close-button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
          border-color: #333;
        }

        .closed-text {
          color: rgba(255, 255, 255, 0.4);
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-family: var(--font-display);
        }

        .error {
          color: #f87171;
          padding: 1rem;
          background: rgba(248, 113, 113, 0.1);
          border-radius: 8px;
        }

        @media (max-width: 768px) {
          .history-container {
            padding: 1rem;
          }

          .history-header {
            flex-direction: column;
            gap: 1rem;
            align-items: flex-start;
          }

          .history-table-container {
            font-size: 0.875rem;
          }

          .history-table th,
          .history-table td {
            padding: 0.5rem;
          }

          .stats-container {
            padding: 16px;
            gap: 16px;
          }

          .stats-group {
            gap: 20px 24px;
          }

          .stat-item {
            min-width: 120px;
          }

          .stat-value {
            font-size: 1.25rem;
          }
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.92);
          backdrop-filter: blur(12px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
          animation: fadeIn 0.2s ease-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .modal-content {
          background: #0a0a0a;
          border: 1px solid #333;
          border-radius: 4px;
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 25px 60px -15px rgba(0, 0, 0, 0.9);
          animation: modalSlideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px;
          border-bottom: 1px solid #333;
          background: #0a0a0a;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 700;
          font-family: var(--font-display);
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #ffffff;
        }

        .modal-close {
          background: transparent;
          border: 1px solid #333;
          color: #ffffff;
          font-size: 1.5rem;
          cursor: pointer;
          padding: 0;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: all 0.2s ease;
          font-family: var(--font-display);
          font-weight: 300;
        }

        .modal-close:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: #444;
          color: #ffffff;
        }

        .modal-close:active {
          background: rgba(255, 255, 255, 0.1);
        }

        .modal-body {
          padding: 24px;
          overflow-y: auto;
          flex: 1;
          background: #0a0a0a;
        }

        .modal-footer {
          padding: 24px;
          border-top: 1px solid #333;
          display: flex;
          justify-content: flex-end;
          background: #0a0a0a;
        }

        .modal-button {
          background: transparent;
          color: #ffffff;
          border: 1px solid #333;
          border-radius: 4px;
          padding: 12px 24px;
          font-size: 0.875rem;
          font-weight: 700;
          font-family: var(--font-display);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .modal-button:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: #444;
        }

        .modal-button:active {
          transform: translateY(0);
        }

        .copy-button:hover {
          background: rgba(255, 255, 255, 0.05) !important;
          border-color: #444 !important;
        }
      `}</style>

      {/* Error Modal */}
      {errorModal && (
        <div className="modal-overlay" onClick={() => setErrorModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ color: '#ffffff' }}>{errorModal.title}</h3>
              <button className="modal-close" onClick={() => setErrorModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="error-message-box">
                <pre style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.875rem',
                  margin: 0,
                  padding: '16px',
                  background: 'rgba(255, 0, 0, 0.1)',
                  border: '1px solid #ff4444',
                  borderRadius: '4px',
                  color: '#ff4444',
                  maxHeight: '300px',
                  overflow: 'auto',
                  lineHeight: '1.6'
                }}>
                  {errorModal.message}
                </pre>
                <button
                  className="copy-button"
                  onClick={(e) => {
                    navigator.clipboard.writeText(errorModal.message);
                    const btn = e.target as HTMLButtonElement;
                    if (btn) {
                      const originalText = btn.textContent;
                      btn.textContent = 'Copied!';
                      setTimeout(() => {
                        btn.textContent = originalText;
                      }, 2000);
                    }
                  }}
                  style={{
                    marginTop: '12px',
                    padding: '10px 16px',
                    background: 'transparent',
                    border: '1px solid #333',
                    borderRadius: '4px',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    fontFamily: 'var(--font-display)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    transition: 'all 0.2s'
                  }}
                >
                  Copy Error
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-button" onClick={() => setErrorModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {successModal && (
        <div className="modal-overlay" onClick={() => setSuccessModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ color: '#ab9ff2' }}>{successModal.title}</h3>
              <button className="modal-close" onClick={() => setSuccessModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="success-message-box">
                <pre style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.875rem',
                  margin: 0,
                  padding: '16px',
                  background: 'rgba(0, 255, 156, 0.1)',
                  border: '1px solid #00FF9C',
                  borderRadius: '4px',
                  color: '#00FF9C',
                  maxHeight: '300px',
                  overflow: 'auto',
                  lineHeight: '1.6'
                }}>
                  {successModal.message}
                </pre>
                <button
                  className="copy-button"
                  onClick={(e) => {
                    navigator.clipboard.writeText(successModal.message);
                    const btn = e.target as HTMLButtonElement;
                    if (btn) {
                      const originalText = btn.textContent;
                      btn.textContent = 'Copied!';
                      setTimeout(() => {
                        btn.textContent = originalText;
                      }, 2000);
                    }
                  }}
                  style={{
                    marginTop: '12px',
                    padding: '10px 16px',
                    background: 'transparent',
                    border: '1px solid #333',
                    borderRadius: '4px',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    fontFamily: 'var(--font-display)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    transition: 'all 0.2s'
                  }}
                >
                  Copy Message
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-button" onClick={() => setSuccessModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default History;
